import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Divider, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, Tabs, Tab,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReorderIcon from '@mui/icons-material/Reorder';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import {
  MY_CEREMONIES_QUERY,
  CATERERS_QUERY,
  MY_CATERING_ORDERS_QUERY,
  CREATE_CATERING_ORDER_MUTATION,
  UPDATE_CATERING_ORDER_MUTATION,
  DELETE_CATERING_ORDER_MUTATION,
  MENU_ITEMS_QUERY,
  ADD_CATERING_ORDER_ITEM_MUTATION,
  REMOVE_CATERING_ORDER_ITEM_MUTATION,
  MY_TASTING_SESSIONS_QUERY,
  CREATE_TASTING_SESSION_MUTATION,
  UPDATE_TASTING_SESSION_MUTATION,
  DELETE_TASTING_SESSION_MUTATION,
} from '../graphql/queries';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';
import { MenuComparison } from './CatererComparisonPage';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { draft: 'default', confirmed: 'success', cancelled: 'error' };
const MEAL_TYPE_LABELS = { full_meal: 'Full Meal', breakfast: 'Breakfast', snacks: 'Snacks', dinner: 'Dinner', sweets_only: 'Sweets Only' };

const EMPTY_ORDER   = { ceremonyId: '', catererId: '', guestCount: '', mealType: 'full_meal', estimatedCost: '', tastingDate: '', notes: '' };
const EMPTY_TASTING = { catererId: '', ceremonyId: '', scheduledAt: '', location: '', attendees: '', notes: '' };
const TASTING_STATUS_COLOR = { scheduled: 'info', completed: 'success', cancelled: 'error', rescheduled: 'warning' };

export default function CateringPage() {
  const [orderDialog, setOrderDialog] = useState(false);
  const [editOrder, setEditOrder]     = useState(null);
  const [orderForm, setOrderForm]     = useState(EMPTY_ORDER);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [tab, setTab] = useState(0);  // 0=Orders 1=Compare 2=Tasting
  const [tastingDialog, setTastingDialog] = useState(false);
  const [editTasting, setEditTasting]     = useState(null);
  const [tastingForm, setTastingForm]     = useState(EMPTY_TASTING);
  const [error, setError] = useState('');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: catererData }  = useQuery(CATERERS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: ordersData, loading, refetch } = useQuery(MY_CATERING_ORDERS_QUERY, { fetchPolicy: 'cache-and-network' });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const caterers   = catererData?.caterers || [];
  const orders     = ordersData?.myCateringOrders || [];
  const selectedOrder = orders.find(o => o.id === selectedOrderId) || null;

  const { data: tastingData, loading: tastingLoading, refetch: refetchTastings } = useQuery(MY_TASTING_SESSIONS_QUERY, {
    skip: tab !== 2,
    fetchPolicy: 'cache-and-network',
  });
  const tastings = tastingData?.myTastingSessions || [];

  const { data: menuData } = useQuery(MENU_ITEMS_QUERY, {
    variables: { catererId: selectedOrder?.catererId || orderForm.catererId },
    skip: !selectedOrder?.catererId && !orderForm.catererId,
    fetchPolicy: 'cache-and-network',
  });
  const menuItems = menuData?.menuItems || [];

  const [createOrder] = useMutation(CREATE_CATERING_ORDER_MUTATION, {
    onCompleted: () => { setOrderDialog(false); setOrderForm(EMPTY_ORDER); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateOrder] = useMutation(UPDATE_CATERING_ORDER_MUTATION, {
    onCompleted: () => { setOrderDialog(false); setEditOrder(null); setOrderForm(EMPTY_ORDER); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteOrder] = useMutation(DELETE_CATERING_ORDER_MUTATION, { onCompleted: () => { setSelectedOrderId(null); refetch(); } });
  const [addItem]    = useMutation(ADD_CATERING_ORDER_ITEM_MUTATION,    { onCompleted: () => refetch() });
  const [removeItem] = useMutation(REMOVE_CATERING_ORDER_ITEM_MUTATION, { onCompleted: () => refetch() });

  const [createTasting] = useMutation(CREATE_TASTING_SESSION_MUTATION, {
    onCompleted: () => { setTastingDialog(false); setTastingForm(EMPTY_TASTING); refetchTastings(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateTasting] = useMutation(UPDATE_TASTING_SESSION_MUTATION, {
    onCompleted: () => { setTastingDialog(false); setEditTasting(null); setTastingForm(EMPTY_TASTING); refetchTastings(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteTasting] = useMutation(DELETE_TASTING_SESSION_MUTATION, { onCompleted: () => refetchTastings() });

  const openCreateTasting = () => { setEditTasting(null); setTastingForm(EMPTY_TASTING); setTastingDialog(true); };
  const openEditTasting   = (t) => {
    setEditTasting(t);
    setTastingForm({ catererId: t.catererId, ceremonyId: t.ceremonyId || '', scheduledAt: t.scheduledAt?.slice(0,16) || '', location: t.location || '', attendees: t.attendees || '', notes: t.notes || '' });
    setTastingDialog(true);
  };
  const handleSaveTasting = () => {
    const vars = { ...tastingForm, ceremonyId: tastingForm.ceremonyId || null, scheduledAt: tastingForm.scheduledAt || null };
    if (editTasting) updateTasting({ variables: { id: editTasting.id, ...vars } });
    else createTasting({ variables: vars });
  };

  const openCreate = () => { setEditOrder(null); setOrderForm(EMPTY_ORDER); setOrderDialog(true); };
  const openEdit   = (o) => {
    setEditOrder(o);
    setOrderForm({ ceremonyId: o.ceremonyId, catererId: o.catererId || '', guestCount: o.guestCount, mealType: o.mealType, estimatedCost: o.estimatedCost || '', tastingDate: o.tastingDate || '', notes: o.notes || '' });
    setOrderDialog(true);
  };

  const handleSave = () => {
    const vars = { ...orderForm, guestCount: Number(orderForm.guestCount) || 0, estimatedCost: orderForm.estimatedCost ? Number(orderForm.estimatedCost) : null, ceremonyId: orderForm.ceremonyId || null, catererId: orderForm.catererId || null };
    if (editOrder) updateOrder({ variables: { id: editOrder.id, ...vars } });
    else createOrder({ variables: vars });
  };

  const toggleMenuItem = (item) => {
    if (!selectedOrderId) return;
    const already = selectedOrder?.items?.find(i => i.menuItemId === item.id);
    if (already) removeItem({ variables: { id: already.id } });
    else addItem({ variables: { orderId: selectedOrderId, menuItemId: item.id, quantity: 1 } });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Catering & Menu</Typography>
          <Typography variant="body2" color="text.secondary">Plan catering orders per ceremony and compare caterers.</Typography>
        </Box>
        {tab === 0 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Order</Button>}
        {tab === 2 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateTasting}>Schedule Tasting</Button>}
      </Stack>

      <ContractedOfferingsPanel categories={['catering']} title="Contracted caterers & menus" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<ReorderIcon />} iconPosition="start" label="My Orders" />
        <Tab icon={<CompareArrowsIcon />} iconPosition="start" label="Compare Caterers" />
        <Tab label="Tasting Sessions" />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ─── Orders Tab ─── */}
      {tab === 0 && (
      <>
      {orders.length === 0 && !loading && (
        <Alert severity="info">No catering orders yet. Click <strong>New Order</strong> to start planning.</Alert>
      )}

      <Grid container spacing={3}>
        {/* Order list */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <Paged items={orders}>{rows => rows.map(o => (
              <Card
                key={o.id}
                sx={{ cursor: 'pointer', border: '2px solid', borderColor: o.id === selectedOrderId ? 'primary.main' : 'divider' }}
                onClick={() => setSelectedOrderId(o.id === selectedOrderId ? null : o.id)}
              >
                <CardContent sx={{ pb: '12px !important' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{o.ceremony?.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{o.caterer?.name || 'No caterer selected'}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                        <Chip label={MEAL_TYPE_LABELS[o.mealType] || o.mealType} size="small" variant="outlined" />
                        <Chip label={`${o.guestCount} guests`} size="small" />
                        <Chip label={o.status} size="small" color={STATUS_COLOR[o.status] || 'default'} />
                      </Stack>
                      {o.estimatedCost && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Est. ₹{Number(o.estimatedCost).toLocaleString('en-IN')}</Typography>}
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(o); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteOrder({ variables: { id: o.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}</Paged>
          </Stack>
        </Grid>

        {/* Menu items panel */}
        <Grid item xs={12} md={8}>
          {!selectedOrder && <Alert severity="info">Select an order to manage its menu items.</Alert>}
          {selectedOrder && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  <RestaurantIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Menu for {selectedOrder.ceremony?.name}
                </Typography>
                {selectedOrder.caterer ? (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedOrder.caterer.name} — click items to add/remove from order
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    {menuItems.length === 0 && <Alert severity="info">No menu items available for this caterer yet.</Alert>}
                    <Grid container spacing={1}>
                      {menuItems.map(item => {
                        const selected = !!selectedOrder.items?.find(i => i.menuItemId === item.id);
                        return (
                          <Grid item xs={12} sm={6} key={item.id}>
                            <Card
                              sx={{ cursor: 'pointer', bgcolor: selected ? 'success.50' : 'background.paper', border: '1px solid', borderColor: selected ? 'success.main' : 'divider' }}
                              onClick={() => toggleMenuItem(item)}
                            >
                              <CardContent sx={{ py: '8px !important', px: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight={selected ? 700 : 400}>{item.itemName}</Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                      <Chip label={item.course} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                      <Chip label="Veg" color="success" variant="outlined" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                                    </Stack>
                                  </Box>
                                  {item.pricePerPerson && <Typography variant="caption" color="text.secondary">₹{item.pricePerPerson}/pp</Typography>}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                ) : (
                  <Alert severity="warning">Edit this order to assign a caterer, then menu items will appear here.</Alert>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create / Edit Dialog */}
      <Dialog open={orderDialog} onClose={() => setOrderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editOrder ? 'Edit Catering Order' : 'New Catering Order'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Ceremony" value={orderForm.ceremonyId} onChange={e => setOrderForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth required>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Caterer" value={orderForm.catererId} onChange={e => setOrderForm(f => ({ ...f, catererId: e.target.value }))} fullWidth>
              <MenuItem value="">— Select later —</MenuItem>
              {caterers.map(c => <MenuItem key={c.id} value={c.id}>{c.name} ({c.city})</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField select label="Meal Type" value={orderForm.mealType} onChange={e => setOrderForm(f => ({ ...f, mealType: e.target.value }))} fullWidth>
                {Object.entries(MEAL_TYPE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
              <TextField label="Guest Count" type="number" value={orderForm.guestCount} onChange={e => setOrderForm(f => ({ ...f, guestCount: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Estimated Cost (₹)" type="number" value={orderForm.estimatedCost} onChange={e => setOrderForm(f => ({ ...f, estimatedCost: e.target.value }))} fullWidth />
              <TextField label="Tasting Date" type="date" value={orderForm.tastingDate} onChange={e => setOrderForm(f => ({ ...f, tastingDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <TextField label="Notes" value={orderForm.notes} onChange={e => setOrderForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOrderDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!orderForm.ceremonyId}>Save</Button>
        </DialogActions>
      </Dialog>
      </> /* end orders tab */
      )}

      {/* ─── Compare Caterers Tab ─── */}
      {/* Compare the contracted caterers' menus for the family's number of plates */}
      {tab === 1 && <MenuComparison />}

      {tab === 2 && (
        <Box>
          {tastingLoading && <LinearProgress sx={{ mb: 2 }} />}
          {tastings.length === 0 && !tastingLoading && (
            <Alert severity="info">No tasting sessions scheduled yet. Click <strong>Schedule Tasting</strong> to book one.</Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Caterer','Ceremony','Date & Time','Location','Attendees','Status','Feedback / Rating','Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tastings.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{t.caterer?.name || '—'}</Typography></TableCell>
                  <TableCell>{t.ceremony?.name || '—'}</TableCell>
                  <TableCell>{t.scheduledAt ? new Date(t.scheduledAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.location || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.attendees || '—'}</TableCell>
                  <TableCell><Chip label={t.status} size="small" color={TASTING_STATUS_COLOR[t.status] || 'default'} /></TableCell>
                  <TableCell>
                    {t.feedback
                      ? <Tooltip title={t.feedback}><Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.rating ? `${t.rating}/5 - ` : ''}{t.feedback}</Typography></Tooltip>
                      : '—'
                    }
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditTasting(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteTasting({ variables: { id: t.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ─── Tasting Session Dialog ─── */}
      <Dialog open={tastingDialog} onClose={() => setTastingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTasting ? 'Edit Tasting Session' : 'Schedule Tasting Session'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField select label="Caterer" value={tastingForm.catererId} onChange={e => setTastingForm(f => ({ ...f, catererId: e.target.value }))} fullWidth required>
                <MenuItem value="">— Select Caterer —</MenuItem>
                {caterers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.city ? ` (${c.city})` : ''}</MenuItem>)}
              </TextField>
              <TextField select label="Ceremony" value={tastingForm.ceremonyId} onChange={e => setTastingForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">— Optional —</MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Scheduled Date & Time" type="datetime-local" value={tastingForm.scheduledAt} onChange={e => setTastingForm(f => ({ ...f, scheduledAt: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Location" value={tastingForm.location} onChange={e => setTastingForm(f => ({ ...f, location: e.target.value }))} fullWidth placeholder="Caterer's banquet hall / test kitchen" />
            </Stack>
            <TextField label="Attendees" value={tastingForm.attendees} onChange={e => setTastingForm(f => ({ ...f, attendees: e.target.value }))} fullWidth placeholder="Bride, Groom, Parents, Chef..." />
            {editTasting && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField select label="Status" value={tastingForm.status || 'scheduled'} onChange={e => setTastingForm(f => ({ ...f, status: e.target.value }))} fullWidth>
                    {['scheduled','completed','cancelled','rescheduled'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                  </TextField>
                  <TextField label="Rating" type="number" value={tastingForm.rating || ''} onChange={e => setTastingForm(f => ({ ...f, rating: e.target.value }))} fullWidth inputProps={{ min: 1, max: 5, step: 0.5 }} placeholder="1–5" />
                </Stack>
                <TextField label="Feedback" value={tastingForm.feedback || ''} onChange={e => setTastingForm(f => ({ ...f, feedback: e.target.value }))} fullWidth multiline rows={2} placeholder="How was the food?" />
              </>
            )}
            <TextField label="Notes" value={tastingForm.notes} onChange={e => setTastingForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTastingDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTasting} disabled={!tastingForm.catererId}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
